import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useAuditEntry, type AuditEntryDetail } from '../../api/audit';
import { Breadcrumbs, SummaryList, Table, type SummaryRow } from '../../components/idsk';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { formatDateTime } from '../../lib/format';
import { QueryError } from '../common/QueryError';
import { LoadingPage } from '../common/LoadingPage';
import { RequireAdmin } from '../common/RequireAdmin';
import { formatValue } from '../history/EntityHistory';
import { ActorName, useAuditLabels } from './auditLabels';

const ENTITY_LINKS: Record<string, string> = { SUBJECT: '/subjekty/', INSPECTOR: '/inspektori/', INSPECTION: '/inspekcie/' };

export function AuditEntryPage() {
  return (
    <RequireAdmin>
      <AuditEntryDetailView />
    </RequireAdmin>
  );
}

interface ChangeRow {
  field: string;
  before?: unknown;
  after?: unknown;
  value?: unknown;
  diff: boolean;
}

/** {field: {before, after}} diffs; other values (e.g. sign-in details) as plain values. */
function changeRows(changes: Record<string, unknown> | null): ChangeRow[] {
  if (!changes) return [];
  return Object.entries(changes).map(([field, v]) => {
    if (v && typeof v === 'object' && !Array.isArray(v) && ('before' in v || 'after' in v)) {
      const d = v as { before?: unknown; after?: unknown };
      return { field, before: d.before, after: d.after, diff: true };
    }
    return { field, value: v, diff: false };
  });
}

function AuditEntryDetailView() {
  const { id } = useParams();
  const { t } = useTranslation();
  const labels = useAuditLabels();
  const query = useAuditEntry(id);
  usePageTitle([t('audit.entryTitle', { id }), t('audit.title')]);
  if (query.error) return <QueryError error={query.error} asPage />;
  if (query.isPending) return <LoadingPage />;
  const e: AuditEntryDetail = query.data;
  const rows = changeRows(e.changes);
  const diffs = rows.filter((r) => r.diff);
  const values = rows.filter((r) => !r.diff);
  const fieldLabel = (field: string) => t(`history.fields.${e.entityType}.${field}`, { defaultValue: t(`audit.detailFields.${field}`, { defaultValue: field }) });
  const link = e.entityType && e.entityId && e.action !== 'DELETE' ? ENTITY_LINKS[e.entityType] : undefined;

  const summary: SummaryRow[] = [
    { key: t('audit.fields.occurredAt'), value: formatDateTime(e.occurredAt) },
    { key: t('audit.fields.action'), value: labels.action(e.action) },
    { key: t('audit.fields.summary'), value: e.summary },
    { key: t('audit.fields.actor'), value: <ActorName actor={e.actor} /> },
    { key: t('audit.fields.roles'), value: e.actor.roles.length ? e.actor.roles.join(', ') : t('common.notSet') },
    { key: t('audit.fields.entityType'), value: e.entityType ? labels.entityType(e.entityType) : t('common.notSet') },
    {
      key: t('audit.fields.entityId'),
      value: e.entityId ? (
        link ? (
          <Link className="govuk-link" to={`${link}${e.entityId}`}>
            {e.entityId}
          </Link>
        ) : (
          e.entityId
        )
      ) : (
        t('common.notSet')
      ),
    },
    { key: t('audit.fields.revision'), value: e.revisionId ?? t('common.notSet') },
    { key: t('audit.fields.requestId'), value: e.requestId ?? t('common.notSet') },
    { key: t('audit.fields.clientIp'), value: e.clientIp ?? t('common.notSet') },
  ];
  if (e.eventKey) summary.push({ key: t('audit.fields.eventKey'), value: e.eventKey });

  return (
    <>
      <Breadcrumbs items={[{ to: '/', text: t('nav.home') }, { to: '/audit', text: t('audit.title') }, { text: t('audit.entryTitle', { id: e.id }) }]} />
      <PageHeading>{t('audit.entryTitle', { id: e.id })}</PageHeading>
      <SummaryList rows={summary} />
      <h2 className="govuk-heading-m">{t('audit.changes')}</h2>
      {rows.length === 0 && <p className="govuk-body">{t('audit.noChanges')}</p>}
      {diffs.length > 0 && (
        <Table<ChangeRow>
          caption={t('audit.diffCaption')}
          captionClassName="govuk-table__caption--s"
          rows={diffs}
          rowKey={(r) => r.field}
          columns={[
            { key: 'field', header: t('audit.fields.field'), rowHeader: true, render: (r) => fieldLabel(r.field) },
            { key: 'before', header: t('audit.fields.before'), render: (r) => formatValue(r.before) || <span className="govuk-hint">{t('history.empty')}</span> },
            { key: 'after', header: t('audit.fields.after'), render: (r) => formatValue(r.after) || <span className="govuk-hint">{t('history.empty')}</span> },
          ]}
        />
      )}
      {values.length > 0 && (
        <Table<ChangeRow>
          caption={t('audit.detailsCaption')}
          captionClassName="govuk-table__caption--s"
          rows={values}
          rowKey={(r) => r.field}
          columns={[
            { key: 'field', header: t('audit.fields.field'), rowHeader: true, render: (r) => fieldLabel(r.field) },
            { key: 'value', header: t('audit.fields.value'), render: (r) => formatValue(r.value) || <span className="govuk-hint">{t('history.empty')}</span> },
          ]}
        />
      )}
    </>
  );
}
